/**
 * Browser OpenTelemetry SDK initialization (tdd-0030).
 *
 * Third tier of the rfc-002 three-runtime split, alongside `./node` and
 * `./web`. Wires `@opentelemetry/sdk-trace-web` + auto-instrumentations so
 * that user clicks, page loads, and outbound `fetch` calls produce spans
 * that chain into the server-side trace via W3C `traceparent`.
 *
 * The heavy SDK is dynamic-imported inside `start()` so apps that don't
 * call `register()` pay zero bundle cost.
 *
 * @example One-liner (typical)
 * ```tsx
 * // apps/<app>/components/observability-init.tsx
 * "use client";
 * import { useEffect } from "react";
 * import { register } from "@workspace/observability/browser";
 *
 * export function ObservabilityInit() {
 *   useEffect(() => { void register({ serviceName: "<app>-web" }); }, []);
 *   return null;
 * }
 * ```
 *
 * @example With overrides
 * ```ts
 * import { createRegister } from "@workspace/observability/browser";
 * export const register = createRegister({
 *   serviceName: "canvas-web",
 *   ignoreUrls: [/\/collab\//],
 * });
 * ```
 */
import { attachBrowserErrorEmitter, installBrowserErrorCapture, SAMPLING_PRIORITY_ATTR, } from "./browser-errors.js";
export { installBrowserErrorCapture, recordBrowserError, } from "./browser-errors.js";
/**
 * Classify the stage from the hostname, matching on **label boundaries**.
 *
 * The previous version detected dev as `host.startsWith("dev.") ||
 * host.includes("-dev.")`, which matches neither of the real dev hostnames
 * (`<app>.dev.gaz.codes`, and `dev.gaz.codes` only by the `startsWith` arm).
 * Every dev page would therefore have classified as **prod** and been sampled
 * at 0.2 instead of 1.0 — silently dropping 80% of dev traces, the opposite of
 * the intent recorded in `79d9cb12c` ("sample prod at 20%, leave local and dev
 * whole"). It never bit only because no dev stage existed yet (tdd-0093).
 *
 * Deliberately duplicates `@workspace/stage-config`'s `detectStageFromHostname`
 * rather than importing it: this package is loaded by every app's
 * `instrumentation.ts` before anything else and currently has **no**
 * `@workspace/*` dependencies. Keep the two in step.
 */
function inferStage() {
    if (typeof window === "undefined")
        return "prod";
    const host = window.location.hostname.toLowerCase().replace(/\.$/, "");
    if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".localhost")) {
        return "local";
    }
    const BASE = "gaz.codes";
    if (host !== BASE && !host.endsWith(`.${BASE}`))
        return "prod";
    const prefix = host === BASE ? "" : host.slice(0, -(BASE.length + 1));
    const stageLabel = prefix === "" ? "" : prefix.split(".").pop();
    if (stageLabel === "l")
        return "local";
    if (stageLabel === "dev")
        return "dev";
    return "prod";
}
/**
 * Raw escape hatch — instantiate the browser SDK with explicit config.
 * Most callers should prefer `register()` or `createRegister()`.
 */
/**
 * The service name tracing actually started with, or null before `start()`.
 *
 * Also the "is tracing live?" flag for `recordEvent` below: a tracer obtained
 * before `provider.register()` has run belongs to the no-op provider and its
 * spans go nowhere, silently. Checking this is what turns "the event vanished"
 * into a one-line warning naming the cause.
 */
let activeServiceName = null;
export async function initBrowserTracing(config) {
    if (typeof window === "undefined")
        return;
    // Normally already installed by <ObservabilityInit>'s module; this covers
    // apps that call register() directly. Idempotent.
    installBrowserErrorCapture();
    // Started before the first await, so it is (a) concurrent with the SDK
    // chunk downloads rather than after them and (b) issued before
    // FetchInstrumentation patches fetch — no span, no traceparent for it.
    const buildSha = config.serviceVersion ? undefined : readBuildSha();
    // `@opentelemetry/context-zone-peer-dep` requires `zone.js` to be loaded
    // as a global before `ZoneContextManager` is constructed. Import for its
    // side effect (installs `Zone` on `globalThis`) before anything else.
    await import("zone.js");
    const [{ WebTracerProvider, BatchSpanProcessor }, { ZoneContextManager }, { OTLPTraceExporter }, { registerInstrumentations }, { FetchInstrumentation }, { XMLHttpRequestInstrumentation }, { DocumentLoadInstrumentation }, { UserInteractionInstrumentation }, { resourceFromAttributes }, { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION, SEMRESATTRS_DEPLOYMENT_ENVIRONMENT, }, { TraceIdRatioBasedSampler, ParentBasedSampler }, otelApi, { PrioritySampler },] = await Promise.all([
        import("@opentelemetry/sdk-trace-web"),
        import("@opentelemetry/context-zone-peer-dep"),
        import("@opentelemetry/exporter-trace-otlp-http"),
        import("@opentelemetry/instrumentation"),
        import("@opentelemetry/instrumentation-fetch"),
        import("@opentelemetry/instrumentation-xml-http-request"),
        import("@opentelemetry/instrumentation-document-load"),
        import("@opentelemetry/instrumentation-user-interaction"),
        import("@opentelemetry/resources"),
        import("@opentelemetry/semantic-conventions"),
        import("@opentelemetry/sdk-trace-web"),
        import("@opentelemetry/api"),
        import("./priority-sampler.js"),
    ]);
    const stage = config.stage ?? inferStage();
    // `NEXT_PUBLIC_OTEL_ENDPOINT` is injected by build-stage.ts (always the prod
    // host: `SHARED_PROD_SERVICES` in @workspace/stage-config); the literal is the
    // fallback and must stay last (tdd-0093).
    // `||` not `??`: an injected-but-empty var must fall through to the
    // default, and it keeps the chain statically collapsible so the built bundle
    // contains exactly one URL — which is what makes the CI "no l.gaz.codes in
    // build output" gate meaningful (tdd-0093).
    const endpoint = config.endpoint || process.env.NEXT_PUBLIC_OTEL_ENDPOINT || "https://otel.gaz.codes";
    // Stage-aware: only prod is sampled. See the field doc on `sampleRate`.
    const sampleRate = config.sampleRate ?? (stage === "prod" ? 0.2 : 1.0);
    // `service.version` may be a PROMISE: resources 2.x accept async attribute
    // values and the BatchSpanProcessor awaits them before the first export, so
    // the build SHA costs SDK start nothing. If it resolves undefined (no
    // `_build.json`, timeout, bad JSON) the attribute is simply omitted.
    //
    // `deployment.environment` is deliberately NOT taken from `_build.json`'s
    // `stage`: the stage is needed synchronously, right here, to pick the
    // sampling rate, and an async label that could disagree with the rate
    // actually applied would make "prod" series that were sampled at 1.0. The
    // hostname is also the truer answer to "where is this page being served".
    const resource = resourceFromAttributes({
        [ATTR_SERVICE_NAME]: config.serviceName,
        [ATTR_SERVICE_VERSION]: config.serviceVersion || buildSha,
        [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: stage,
        ...config.resourceAttributes,
    });
    // ParentBased semantics unchanged; only the ROOT decision gains the
    // `sampling.priority` bypass (see priority-sampler.ts).
    const sampler = new ParentBasedSampler({
        root: new PrioritySampler(new TraceIdRatioBasedSampler(sampleRate)),
    });
    const provider = new WebTracerProvider({
        resource,
        sampler,
        spanProcessors: [
            new BatchSpanProcessor(new OTLPTraceExporter({ url: `${endpoint}/v1/traces` })),
        ],
    });
    provider.register({ contextManager: new ZoneContextManager() });
    // From here a tracer resolves to the real provider, so `recordEvent` works.
    activeServiceName = config.serviceName;
    // ...and errors captured since <ObservabilityInit> evaluated can be flushed.
    attachBrowserErrorEmitter(otelApi, config.serviceName);
    const defaultIgnoreUrls = [
        /\/v1\/traces$/,
        /\/v1\/metrics$/,
        /\/v1\/logs$/,
    ];
    const ignoreUrls = [...defaultIgnoreUrls, ...(config.ignoreUrls ?? [])];
    // FetchInstrumentation treats every cross-origin request as no-propagate
    // unless its origin matches `propagateTraceHeaderCorsUrls`. `*.l.gaz.codes`
    // subdomains share eTLD+1 but browsers count them as cross-origin, so
    // every inter-service call would lose the trace header. Default to our
    // own subdomain spaces (local + prod); callers can extend.
    const defaultPropagateCorsUrls = [
        /^https?:\/\/[^/]*\.l\.gaz\.codes(\/|$)/,
        /^https?:\/\/[^/]*\.gaz\.codes(\/|$)/,
    ];
    const propagateTraceHeaderCorsUrls = [
        ...defaultPropagateCorsUrls,
        ...(config.propagateTraceHeaderCorsUrls ?? []),
    ];
    const instrumentations = [];
    if (config.instrumentFetch !== false) {
        instrumentations.push(new FetchInstrumentation({
            ignoreUrls,
            propagateTraceHeaderCorsUrls,
            clearTimingResources: true,
        }));
    }
    if (config.instrumentXHR === true) {
        instrumentations.push(new XMLHttpRequestInstrumentation({
            ignoreUrls,
            propagateTraceHeaderCorsUrls,
        }));
    }
    if (config.instrumentDocumentLoad !== false) {
        instrumentations.push(new DocumentLoadInstrumentation());
    }
    if (config.instrumentUserInteraction !== false) {
        instrumentations.push(new UserInteractionInstrumentation({
            eventNames: ["click", "submit"],
        }));
    }
    registerInstrumentations({ instrumentations });
    if (config.instrumentWebVitals !== false) {
        void emitWebVitalsAsSpans(config.serviceName);
    }
    // eslint-disable-next-line no-console
    console.log(`[Observability] Browser tracing initialized for "${config.serviceName}" → ${endpoint} (stage=${stage}, sample=${sampleRate})`);
}
/**
 * Subscribe to Core Web Vitals and emit a tiny carrier span per metric.
 * Each span is named `web-vital` with attributes:
 * - `web.vital.name`: CLS | FCP | INP | LCP | TTFB
 * - `web.vital.value`: numeric value (ms for timings, unitless for CLS)
 * - `web.vital.rating`: good | needs-improvement | poor
 * - `web.vital.navigation_type`: navigate | reload | back-forward | ...
 *
 * Vitals fire post-load and can arrive long after the documentLoad span
 * has ended, so they get their own short-lived spans instead.
 */
async function emitWebVitalsAsSpans(serviceName) {
    if (typeof window === "undefined")
        return;
    try {
        const [{ onCLS, onFCP, onINP, onLCP, onTTFB }, { trace }] = await Promise.all([import("web-vitals"), import("@opentelemetry/api")]);
        const tracer = trace.getTracer(`${serviceName}/web-vitals`);
        const emit = (name) => (metric) => {
            // Priority + root: vitals are five spans per page view, and at the
            // prod 0.2 ratio they were undercounted 5x (tdd-0110). `root` so an
            // unsampled ambient context cannot drop them via ParentBased.
            const span = tracer.startSpan("web-vital", {
                root: true,
                attributes: {
                    "web.vital.name": name,
                    "web.vital.value": metric.value,
                    "web.vital.rating": metric.rating,
                    "web.vital.navigation_type": metric.navigationType,
                    [SAMPLING_PRIORITY_ATTR]: 1,
                },
            });
            span.end();
        };
        onCLS(emit("CLS"));
        onFCP(emit("FCP"));
        onINP(emit("INP"));
        onLCP(emit("LCP"));
        onTTFB(emit("TTFB"));
    }
    catch {
        // web-vitals dep may not be installed in every consumer; silently skip.
    }
}
/**
 * Best-effort read of `/_build.json` (tdd-0110 contract) for the build SHA.
 * Never throws, never takes longer than `timeoutMs`; anything unexpected —
 * 404 in local dev, an HTML fallback page, a malformed body — is `undefined`.
 */
export async function readBuildSha(timeoutMs = 3000) {
    if (typeof fetch !== "function")
        return undefined;
    const controller = typeof AbortController === "function" ? new AbortController() : undefined;
    const timer = setTimeout(() => controller?.abort(), timeoutMs);
    try {
        const res = await fetch("/_build.json", {
            cache: "no-store",
            credentials: "omit",
            signal: controller?.signal,
        });
        if (!res.ok)
            return undefined;
        if ((res.headers.get("content-type") ?? "").includes("html"))
            return undefined;
        const body = await res.json();
        const sha = body?.sha;
        return typeof sha === "string" && /^[0-9a-f]{7,64}$/i.test(sha) ? sha : undefined;
    }
    catch {
        return undefined;
    }
    finally {
        clearTimeout(timer);
    }
}
let started = false;
function construct(config) {
    return {
        start: async () => {
            if (started)
                return;
            started = true;
            try {
                await initBrowserTracing(config);
            }
            catch (err) {
                started = false;
                // eslint-disable-next-line no-console
                console.warn("[Observability] Browser tracing init failed", err);
            }
        },
    };
}
let _default = null;
/**
 * Factory for the rfc-002 three-tier pattern.
 *
 * - Called with no args: returns the cached default singleton. Requires
 *   `serviceName` to come from `OTEL_SERVICE_NAME`-equivalent env injected
 *   into the bundle (e.g. via `NEXT_PUBLIC_OTEL_SERVICE_NAME`), or a
 *   `serviceName` override passed in.
 * - Called with overrides: returns a fresh instance with those overrides.
 */
export function createBrowserObservability(config) {
    if (!config) {
        if (!_default)
            _default = construct(resolveDefaults({}));
        return _default;
    }
    return construct(resolveDefaults(config));
}
function resolveDefaults(overrides) {
    const envServiceName = typeof process !== "undefined"
        ? process.env.NEXT_PUBLIC_OTEL_SERVICE_NAME
        : undefined;
    return {
        serviceName: overrides.serviceName ?? envServiceName ?? "unknown-web",
        ...overrides,
    };
}
/**
 * Default configured singleton. Equivalent to `createBrowserObservability()`.
 */
export const browserObservability = createBrowserObservability();
/**
 * Start the default singleton. Idempotent.
 *
 * @example
 * ```ts
 * import { register } from "@workspace/observability/browser";
 * useEffect(() => { void register({ serviceName: "canvas-web" }); }, []);
 * ```
 *
 * Note: this `register` accepts a one-shot config so callers can pass
 * `serviceName` without building a custom factory. Passing config after the
 * singleton has already started has no effect.
 */
export async function register(config) {
    if (config) {
        await createBrowserObservability(config).start();
        return;
    }
    await browserObservability.start();
}
/**
 * Record a client-side event as a tiny carrier span.
 *
 * ## What this is for
 *
 * The browser tier auto-instruments fetch, XHR, document load and user
 * interaction — it sees every HTTP call an app makes and nothing about what
 * the app CONCLUDED. That gap is not academic: chat computes whether each
 * attachment got a storage key (the single most useful signal for "the model
 * cannot see my image") and could only write it to `console.info`, where it is
 * visible to whoever has devtools open at the time and to nobody afterwards.
 * A failed upload likewise removed the file from the UI and told no one.
 *
 * The shape deliberately mirrors `web-vital`: a short-lived named span whose
 * attributes carry the payload. That already works end to end here — it
 * batches, exports and joins the session's trace — so this adds an API, not a
 * mechanism.
 *
 * ## Keep attributes low-cardinality
 *
 * These become span attributes, so an id or a filename per event is a new
 * series each time. Record the OUTCOME and the COUNT — `keyed: 2, total: 3` —
 * not the keys. A one-off identifier belongs in the message you log alongside.
 *
 * ## Never throws
 *
 * Telemetry must not be the thing that breaks a send. Before `register()` has
 * run — or if it failed, or tracing is disabled — this warns once per event
 * name and returns. It does not queue: an event buffered before start and
 * flushed later would carry a misleading timestamp, and the events worth
 * recording here all happen well after page load.
 *
 * @example
 * ```ts
 * recordEvent("chat.attachments.sent", { total: 3, keyed: 2 });
 * recordEvent("chat.upload.failed", { reason: "quota" }, { error: true });
 * ```
 */
export function recordEvent(name, attributes = {}, options = {}) {
    if (typeof window === "undefined")
        return;
    if (!activeServiceName) {
        warnOnce(name, `[Observability] recordEvent("${name}") ignored — browser tracing has not started. ` +
            "Call register() (apps/*/app/layout.tsx mounts <ObservabilityInit />) before recording events.");
        return;
    }
    // Loaded lazily and not awaited: callers are on interaction paths and must
    // not take an import on the critical path. The SDK is already in the bundle
    // by this point, so this resolves from module cache.
    void import("@opentelemetry/api")
        .then(({ trace, SpanStatusCode }) => {
        const clean = {};
        for (const [k, v] of Object.entries(attributes)) {
            // Dropped rather than stringified to "undefined", which reads as a real
            // value in a query and is worse than an absent attribute.
            if (v !== undefined)
                clean[k] = v;
        }
        const span = trace
            .getTracer(`${activeServiceName}/events`)
            .startSpan(name, { attributes: clean });
        if (options.error)
            span.setStatus({ code: SpanStatusCode.ERROR });
        span.end();
    })
        .catch(() => {
        // Recording a diagnostic must never become the incident.
    });
}
const warned = new Set();
/** One warning per event name — a send loop must not flood the console. */
function warnOnce(key, message) {
    if (warned.has(key))
        return;
    warned.add(key);
    // eslint-disable-next-line no-console
    console.warn(message);
}
/**
 * Build a `register` function bound to overrides — mirrors the `./web`
 * shape. Use when an app needs custom config beyond `serviceName`.
 */
export function createRegister(overrides) {
    const instance = createBrowserObservability(overrides);
    return () => instance.start();
}
